import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null); // { modules: {...}, pages: {...} }
  // Daily Work Reports role: { is_supervisor, is_management, expected_to_report, supervisor, team, options }
  const [dwr, setDwr] = useState(null);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/permissions/me");
      setPermissions(data);
    } catch {
      setPermissions({ modules: {}, pages: {} });
    }
    api.get("/dwr/me").then(({ data }) => setDwr(data)).catch(() => setDwr(null));
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await fetchPermissions();
    } catch {
      setUser(false);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) localStorage.setItem("kn_token", data.access_token);
      setUser(data.user);
      await fetchPermissions();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("kn_token");
    setUser(false);
    setPermissions(null);
  };

  const forgotPassword = async (email) => {
    try {
      await api.post("/auth/forgot-password", { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // SuperAdmin always short-circuits to "everything allowed" client-side too
  // (matches permissions.py's own SuperAdmin bypass), so the UI never waits
  // on /permissions/me resolving a matrix that would say the same thing.
  const can = (module, action) => {
    if (user?.role === "SuperAdmin") return true;
    return !!permissions?.modules?.[module]?.[action];
  };
  const isFieldHidden = (module, field) => {
    if (user?.role === "SuperAdmin") return false;
    return !!permissions?.modules?.[module]?.hidden_fields?.includes(field);
  };
  const canViewPage = (page) => {
    if (user?.role === "SuperAdmin") return true;
    return !!permissions?.pages?.[page];
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, forgotPassword, refresh: fetchMe,
      permissions, can, isFieldHidden, canViewPage, dwr,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const canWrite = (user) => user?.role === "SuperAdmin";
